import { Reference } from '@apollo/client';
import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useUnblockCommunityUserMutation } from 'generated/graphql';
import { CommunityId } from '../model/community.types';
import { AuthorId } from '../model/post.types';
import { matchesCommunityArg } from '../lib/cacheFields';

type UnblockUserArgs = {
  communityId: CommunityId;
  userId: AuthorId;
};

export const useUnblockUser = () => {
  const [unblockUserMutation] = useUnblockCommunityUserMutation();

  return useCallback(
    ({ communityId, userId }: UnblockUserArgs) =>
      unblockUserMutation({
        variables: { communityId, userId },
        optimisticResponse: {
          __typename: 'Mutation',
          unblockUser: true,
        },
        update: (cache) => {
          cache.modify<{ blockedUsers: Reference[] | null }>({
            fields: {
              blockedUsers: (
                // The resolver returns null, not [], for a community with no
                // blocked users.
                existing: readonly Reference[] | null = [],
                { storeFieldName, readField },
              ) =>
                matchesCommunityArg(storeFieldName, communityId)
                  ? (existing ?? []).filter(
                      (ref) => readField('id', ref) !== userId,
                    )
                  : existing,
            },
          });
          cache.modify({
            id: cache.identify({ __typename: 'Community', id: communityId }),
            fields: {
              blockedUsersCount: (count: number) => Math.max(0, count - 1),
            },
          });
        },
      })
        .then(() => {
          toast.success('User unblocked');
        })
        .catch(() => {
          toast.error('Something went wrong');
        }),
    [unblockUserMutation],
  );
};
